import { Component, inject, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseViewComponent } from '../base-view.component';
import { StateChangedModel } from '../../common/services/state.service';
import { SecureDataService } from '../../common/services/securedata.sevice';
import { PhoneVerificationComponent } from '../../common/modules/phone-verification/phone-verification.component';
import { LanguageStateService } from '../../common/services/language-state.service';
import { finalize, firstValueFrom, take } from 'rxjs';
import { Message } from '../../common/services/device.service';
import { VIAccountResponse, VIResponseModel, WirelessSetupService } from '../../common/services/wireless-setup.service';
import { CommonUtil } from '../../common/util/common-util';

@Component({
    selector: 'app-wireless-setup',
    templateUrl: './wireless-setup.component.html',
    styleUrl: './wireless-setup.component.scss',
    standalone: false
})
export class WirelessSetupComponent extends BaseViewComponent {
  fb: FormBuilder = inject(FormBuilder)
  #secureDataService: SecureDataService = inject(SecureDataService)
  languageService: LanguageStateService = inject(LanguageStateService)
  wirelessSetupService = inject(WirelessSetupService)
  showCancelDialog = false;
  @ViewChild(PhoneVerificationComponent) phoneVerificationComponent: PhoneVerificationComponent
  wirelessAccountAuthenticationForm: FormGroup
  pinRequested: boolean;
  showOtpAndPhoneFields: boolean;

  showSearchButton = signal<boolean>(true)
  showZipAndSsnFields = signal<boolean>(false)
  showVerifyAccountButton = signal<boolean>(false)
  showAuthenticationSection = signal<boolean>(false)
  showRequestPinButton = signal<boolean>(false)
  showPinAndAccountPasscodeFields = signal<boolean>(false)
  showSubmitPinButton = signal<boolean>(false)
  fdeMessage: string = ''
  fdeCode: string = ''
  accountNumber: number
  wirelessSetupCustomerOrderPayload:any;
  isOneTimePinSelected = signal<boolean>(true)
  isAccountPasscodeSelected = signal<boolean>(false)
  isSubmitPinSuceessfully = false;
  authUserList: any;





  constructor() {
    super()

  }


  protected override refreshView(): void {

    this.wirelessAccountAuthenticationForm = this.fb.group({
      attPhoneNumber: ['', [Validators.required, Validators.pattern(/^\d{3}-\d{3}-\d{4}$/)]],
      attBillingZip: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      last4Ssn: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
      oneTimePinOrAccountPasscode: ['oneTimePin', [Validators.required]],
      oneTimePin: ['', [Validators.required]],
      accountPasscode: ['', [Validators.required]],
    })

    this.isOneTimePinSelected.set(true)
    this.isAccountPasscodeSelected.set(false)
    this.setupFormSubscription()
  }

  setupFormSubscription() {
    this.subscriptions.add(
      this.wirelessAccountAuthenticationForm.get('oneTimePinOrAccountPasscode')?.valueChanges.subscribe(value => {
        this.modeChanged(value)
      })
    )
  }

  modeChanged(currentValue: 'oneTimePin' | 'accountPasscode') {
    this.wirelessAccountAuthenticationForm.patchValue(
      { oneTimePin: '', accountPasscode: '' },
      { emitEvent: false }
    );

    this.isOneTimePinSelected.set(currentValue === 'oneTimePin');
    this.isAccountPasscodeSelected.set(currentValue === 'accountPasscode');

    if (currentValue === 'accountPasscode') {
      this.showRequestPinButton.set(false)
      this.showPinAndAccountPasscodeFields.set(true)
      this.showSubmitPinButton.set(true)
    } else {
      this.showRequestPinButton.set(true)
      this.showPinAndAccountPasscodeFields.set(false)
      this.showSubmitPinButton.set(false)
    }
  }

  protected override onSecureDataChanged(val: StateChangedModel): void {
    if (val.Type == 'WirelessSetup' && val.Data != undefined) {
      const data = val.Data as any;
      this.wirelessSetupCustomerOrderPayload = data?.wirelessSetupCustomerOrderPayload || {};

    }
  }
  

  searchClicked() {
    if (!this.wirelessAccountAuthenticationForm.get('attPhoneNumber')?.valid) {
      this.wirelessAccountAuthenticationForm.get('attPhoneNumber')?.markAsTouched()
      return
    }

    if (!this.wirelessSetupCustomerOrderPayload) {
      this.reloadCurrentStatus()
      return
    }

    this.spinnerService.setLoading(true, 'wireless-account-authentication-viGetAuthData')
    this.subscriptions.add(
      this.wirelessSetupService.getAuthData({TransactionNumber: this.sanitizePhoneNumber() }, this.wirelessSetupCustomerOrderPayload)
        .pipe(
          finalize(() => {
            this.spinnerService.setLoading(false, 'wireless-account-authentication-viGetAuthData')
          })
        )
        .subscribe({
          next: (resp: any) => {
            const viResp = this.retrieveWirelessResponse(resp)
            if (viResp.Code === '0000') {
              this.authUserList = viResp.AuthUserList?.split(',').map(auth => auth.trim())

              this.accountNumber = viResp.AccountNumber
              this.wirelessAccountAuthenticationForm.get('attPhoneNumber')?.disable()
              this.showSearchButton.set(false)
              this.showZipAndSsnFields.set(true)
              this.showVerifyAccountButton.set(true)
            } else {
              this.notificationService.showError(viResp?.Message || 'Unable to find account. Please verify the phone number.')
            }
          }
        })
    )
  }

  /**
   * Browser history (back/reload) can render this page without the WirelessSetup status that
   * carries the order payload. Re-read the current status and follow it instead of calling
   * authgetdata without the order.
   */
  async reloadCurrentStatus(): Promise<void> {
    this.spinnerService.setLoading(true, 'wireless-setup-reloadCurrentStatus')
    try {
      const status = await firstValueFrom(this.#secureDataService.getStatus())
      if (!status) {
        this.navigateWaiting()
      } else if (status.LocationValidationStatus !== 'Allowed') {
        await this.navigateLocationRequired()
      } else if (status.Type !== 'WirelessSetup') {
        this.navigateToSecureDataType(status.Type)
      } else if (this.wirelessSetupCustomerOrderPayload) {
        this.searchClicked()
      } else {
        this.notificationService.showError(Message.ErrorMessage)
      }
    } catch {
      // The HTTP error interceptor already shows the error, including the expired-link message.
    } finally {
      this.spinnerService.setLoading(false, 'wireless-setup-reloadCurrentStatus')
    }
  }

  verifyAccountClicked() {
    if (!this.wirelessAccountAuthenticationForm.get('attBillingZip')?.valid ||
      !this.wirelessAccountAuthenticationForm.get('last4Ssn')?.valid) {
      this.wirelessAccountAuthenticationForm.get('attBillingZip')?.markAsTouched()
      this.wirelessAccountAuthenticationForm.get('last4Ssn')?.markAsTouched()
      return
    }

    this.spinnerService.setLoading(true, 'wireless-account-authentication-viGetAccount')
    const formValues = this.wirelessAccountAuthenticationForm.value
    const body = {
      TransactionNumber: this.sanitizePhoneNumber(),
      BillingZipCode: formValues.attBillingZip,
      Last4SSN: formValues.last4Ssn
    }

    this.subscriptions.add(
      this.wirelessSetupService.getAccount(body, this.wirelessSetupCustomerOrderPayload)
        .pipe(
          finalize(() => {
            this.spinnerService.setLoading(false, 'wireless-account-authentication-viGetAccount')
          })
        )
        .subscribe({
          next: (resp: any) => {
            const viResp = this.retrieveWirelessResponse(resp)


            if(viResp.FdeActionRequired) {
              this.wirelessAccountAuthenticationForm.get('attBillingZip')?.disable()
              this.wirelessAccountAuthenticationForm.get('last4Ssn')?.disable()
              this.fdeMessage = viResp.FdeMessage
              this.fdeCode = viResp.FdeCode
              this.showVerifyAccountButton.set(false)
              this.showAuthenticationSection.set(true)
              this.showRequestPinButton.set(true)
            } else {
              this.notificationService.showError(viResp?.Message || 'Unable to verify account details. Please check your information.')
            }
          }
        })
    )
  }

  showOnetimePinOption() {
    return this.fdeCode === '100' || this.fdeCode === '101';
  }

  showPasscodeOption() {
    return this.fdeCode === '101';
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    return CommonUtil.onlyNumbers(event)
  }


  private sanitizePhoneNumber() {
    return this.wirelessAccountAuthenticationForm.get('attPhoneNumber')?.value.replace(/-/g, '')
  }

  private retrieveWirelessResponse(resp: any) {
    return resp.ResponseHeader?.ProductResponse?.find(
      (response: any) => response.Type.toLowerCase() === 'wireless'
    )
  }

  submitPinClicked() {
    const formValues = this.wirelessAccountAuthenticationForm.getRawValue()

    if ((this.fdeCode === '100' || this.fdeCode === '101') && this.isOneTimePinSelected() &&
      !this.wirelessAccountAuthenticationForm.get('oneTimePin')?.valid) {
      return
    }
    if (this.fdeCode === '101' && this.isAccountPasscodeSelected() &&
      !this.wirelessAccountAuthenticationForm.get('accountPasscode')?.valid) {
      return
    }

    this.spinnerService.setLoading(true, 'wireless-account-authentication-submitPin')

    const body = {
      TransactionNumber: this.sanitizePhoneNumber(),
      BillingZipCode: formValues.attBillingZip,
      Last4SSN: formValues.last4Ssn,
    } as any

    if (this.isOneTimePinSelected()) {
      body.Pin = formValues.oneTimePin
    }
    if (this.fdeCode === '101' && this.isAccountPasscodeSelected()) {
      body.PassCode = formValues.accountPasscode
    }

    this.subscriptions.add(
      this.wirelessSetupService.getAccount(body, this.wirelessSetupCustomerOrderPayload)
        .pipe(
          finalize(() => {
            this.spinnerService.setLoading(false, 'wireless-account-authentication-submitPin')
          })
        )
        .subscribe({
          next: (resp: any) => {
            this.isSubmitPinSuceessfully = true;
            const viResp = this.retrieveWirelessResponse(resp)
            if (viResp.Code === '0000') {
              this.onAccountAuthenticationComplete(resp)
            } 
            else if(viResp.Code === '5000'){
              this.fdeCode = viResp.FdeCode
              this.isAccountPasscodeSelected.set(true);
            }
            else {
              this.notificationService.showError(viResp?.Message || 'Failed to authenticate account. Please try again.')
            }
          }
        })
    )
  }

  onAccountAuthenticationComplete(response?: VIResponseModel<VIAccountResponse> ) {
    this.notificationService.showSuccess('Account authentication completed successfully.')
    this.submitClicked(response);

  }

  requestPinClicked() {
    this.spinnerService.setLoading(true, 'wireless-account-authentication-requestPin')
    this.subscriptions.add(
      this.wirelessSetupService.sendPin({
        TransactionNumber: this.sanitizePhoneNumber(),
        AccountNumber: this.accountNumber
      }, this.wirelessSetupCustomerOrderPayload)
        .pipe(
          finalize(() => {
            this.spinnerService.setLoading(false, 'wireless-account-authentication-requestPin')
          })
        )
        .subscribe({
          next: (resp: any) => {
            const viResp = this.retrieveWirelessResponse(resp)
            if (viResp.Code === '0000') {
              this.showRequestPinButton.set(false)
              this.showPinAndAccountPasscodeFields.set(true)
              this.showSubmitPinButton.set(true)
              this.notificationService.showSuccess('PIN has been sent to your phone.')
            } else if (viResp.Code === '0001') {
              this.wirelessAccountAuthenticationForm.get('oneTimePin')?.disable()
              this.wirelessAccountAuthenticationForm.patchValue({
                oneTimePinOrAccountPasscode: 'accountPasscode'
              })
              this.showRequestPinButton.set(false)
              this.showPinAndAccountPasscodeFields.set(true)
              this.showSubmitPinButton.set(true)
              this.notificationService.showError(viResp?.Message || 'Unable to send PIN. Please try again.')
            }
          }
        })
    )
  }

  formatPhone(event: any, controlName: string) {
    const control = this.wirelessAccountAuthenticationForm.get(controlName);
    if (!control) return;

    const input = event.target.value.replace(/\D/g, '').substring(0, 10);

    // Early return if no input
    if (!input) {
      control.setValue('', { emitEvent: false });
      return;
    }

    const areaCode = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6);

    let formattedValue = '';

    if (input.length > 6) {
      formattedValue = `${areaCode}-${middle}-${last}`;
    } else if (input.length > 3) {
      formattedValue = `${areaCode}-${middle}`;
    } else {
      formattedValue = areaCode;
    }

    // Only update if the value actually changed
    if (control.value !== formattedValue) {
      control.setValue(formattedValue, { emitEvent: false });

      // Set cursor position
      setTimeout(() => {
        const inputElement = event.target as HTMLInputElement;
        inputElement.setSelectionRange(formattedValue.length, formattedValue.length);
      });
    }
  }


  submitClicked(response: VIResponseModel<VIAccountResponse>) {
    const pinOk = this.isOneTimePinSelected() && this.wirelessAccountAuthenticationForm.get('oneTimePin')?.value;
    const passcodeOk = this.isAccountPasscodeSelected() && this.wirelessAccountAuthenticationForm.get('accountPasscode')?.value;

    if ((pinOk || passcodeOk) && this.isSubmitPinSuceessfully) {
      const data = { authUserList: this.authUserList, response };
      this.#secureDataService.submitCustomerData(data, 'WirelessSetup')
        .subscribe({
          next: () => {
            this.navigateWaiting();
          },
          error: (err) => {
            this.notificationService.showError(err);
          }
        });
    } else {
      this.wirelessAccountAuthenticationForm?.markAllAsTouched();
      this.scrollToInvalidControl();
      return;
    }
  }

  onCancelClick() {
    this.showCancelDialog = true;
  }

  onCancel(data: unknown) {
    this.showCancelDialog = false;
    if (data && (data as { data: { success: boolean } }).data?.success) {
      this.#secureDataService.cancel('WirelessSetup-Cancel').pipe(take(1)).subscribe({
        next: () => {
          this.navigateWaiting(true);
        },
        error: (err) => this.onCancelError('WirelessSetup-Cancel', err)
      });
    }
  }



}
